import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import userProfileService from '../../services/userProfileService';
import '../OrganizerDashboard/OrganizerSettings.css';
import './Profile.css';

const SOCIAL_FIELDS = [
  { id: 'instagram', label: 'Instagram', prefix: 'instagram.com/' },
  { id: 'twitter', label: 'X / Twitter', prefix: 'x.com/' },
  { id: 'youtube', label: 'YouTube', prefix: 'youtube.com/@' },
  { id: 'tiktok', label: 'TikTok', prefix: 'tiktok.com/@' },
  { id: 'linkedin', label: 'LinkedIn', prefix: 'linkedin.com/' },
  { id: 'website', label: 'Personal Website', prefix: 'https://', isWebsite: false }
];

const SECURITY_ROWS = [
  {
    title: 'Account Password',
    description: 'You have not set up a password for your account.',
    action: 'Set password'
  },
  {
    title: 'Two-Factor Authentication',
    description: 'Add an extra layer of security to your account.',
    action: 'Enable 2FA'
  },
  {
    title: 'Passkeys',
    description: 'Passkeys are a secure and convenient way to sign in.',
    action: 'Add passkey'
  }
];

const THIRD_PARTY_ROWS = [
  { name: 'Google', linked: 'google' },
  { name: 'Facebook', linked: false },
  { name: 'Apple', linked: false },
  { name: 'Google', linked: false }
];

const AccountDetails = () => {
  const { currentUser, userProfile } = useAuth();
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    bio: '',
    organisation: '',
    jobTitle: ''
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const loadUserProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      setError(null);

      let backendUserId = userProfile?.backend_user_id || userProfile?.firebase_uid || currentUser?.uid;
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser.id) backendUserId = parsedUser.id;
        } catch {
          // ignore
        }
      }

      if (!backendUserId) {
        throw new Error('No backend user ID available for fetching profile');
      }

      userProfileService.setUser({ uid: backendUserId });
      const profile = await userProfileService.getUserProfile();

      setProfileData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phone: profile.phone || '',
        username: profile.username || '',
        bio: profile.bio || '',
        organisation: profile.organisation || profile.company || '',
        jobTitle: profile.jobTitle || profile.job_title || ''
      });
    } catch {
      setError('Failed to load profile data. Using cached information.');
      if (currentUser) {
        setProfileData((prev) => ({
          ...prev,
          firstName: prev.firstName || currentUser.displayName?.split(' ')[0] || '',
          lastName:
            prev.lastName || currentUser.displayName?.split(' ').slice(1).join(' ') || '',
          email: prev.email || currentUser.email || ''
        }));
      }
    } finally {
      setProfileLoading(false);
    }
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (currentUser) {
      const jwtToken = localStorage.getItem('access_token');

      if (userProfile) {
        setProfileData({
          firstName: userProfile.first_name || userProfile.firstName || '',
          lastName: userProfile.last_name || userProfile.lastName || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
          username: userProfile.username || '',
          bio: userProfile.bio || '',
          organisation: userProfile.organisation || userProfile.company || '',
          jobTitle: userProfile.jobTitle || userProfile.job_title || ''
        });
      }

      if (jwtToken) {
        userProfileService.setAuthToken(jwtToken);
        userProfileService.setUser(currentUser);
        loadUserProfile();
      } else {
        setError('Authentication error. Please try logging in again.');
        setProfileLoading(false);
      }
    }
  }, [currentUser, userProfile, loadUserProfile]);

  const handleProfileUpdate = async (e) => {
    e?.preventDefault?.();
    try {
      setError(null);
      setSuccessMessage('');

      if (profileData.phone?.trim()) {
        const digits = profileData.phone.replace(/\D/g, '');
        if (digits.length < 9) {
          setError('Phone number must have at least 9 digits.');
          return;
        }
      }

      const updatedProfile = await userProfileService.updateUserProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        bio: profileData.bio,
        organisation: profileData.organisation,
        jobTitle: profileData.jobTitle
      });

      setProfileData((prev) => ({
        ...prev,
        firstName: updatedProfile.firstName || '',
        lastName: updatedProfile.lastName || '',
        email: updatedProfile.email || '',
        phone: updatedProfile.phone || '',
        bio: updatedProfile.bio || '',
        organisation: updatedProfile.organisation || updatedProfile.company || '',
        jobTitle: updatedProfile.jobTitle || updatedProfile.job_title || ''
      }));

      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setError('Failed to update profile. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const profileInitial =
    (profileData.firstName || profileData.email || 'U').charAt(0).toUpperCase();

  const googleLinked =
    currentUser?.providerData?.some((p) => p.providerId?.includes('google')) ||
    Boolean(currentUser?.email);

  if (profileLoading) {
    return (
      <div className="org-settings-loading">
        <div className="loading-spinner" />
        <p>Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="org-account-settings">
      {error ? (
        <div className="profile-message error-message org-account-message">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="close-button" aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}
      {successMessage ? (
        <div className="profile-message success-message org-account-message">
          <span>{successMessage}</span>
        </div>
      ) : null}

      <form className="org-account-form" onSubmit={handleProfileUpdate}>
        <section className="org-account-section">
          <h2 className="org-account-section-title">Your profile</h2>

          <div className="org-account-row org-account-row--split">
            <div className="org-account-field">
              <label htmlFor="firstName" className="org-account-label">
                First name <span className="org-account-required">*</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                className="org-account-input"
                value={profileData.firstName}
                onChange={handleInputChange}
                placeholder="John"
              />
            </div>
            <div className="org-account-field">
              <label htmlFor="lastName" className="org-account-label">
                Last name <span className="org-account-required">*</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                className="org-account-input"
                value={profileData.lastName}
                onChange={handleInputChange}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="org-account-row org-account-row--profile-meta">
            <div className="org-account-field org-account-field--grow">
              <label htmlFor="username" className="org-account-label">
                Username <span className="org-account-required">*</span>
              </label>
              <div className="org-account-input-prefix-wrap">
                <span className="org-account-input-prefix">@</span>
                <input
                  id="username"
                  name="username"
                  type="text"
                  className="org-account-input org-account-input--prefixed"
                  value={profileData.username}
                  onChange={handleInputChange}
                  placeholder="username"
                  readOnly
                  aria-readonly="true"
                />
              </div>
            </div>
            <div className="org-account-field org-account-field--avatar">
              <span className="org-account-label">Profile picture</span>
              <div className="org-account-avatar" aria-hidden="true">
                {currentUser?.photoURL ? (
                  <img src={currentUser.photoURL} alt="" />
                ) : (
                  <span className="org-account-avatar-fallback">{profileInitial}</span>
                )}
              </div>
            </div>
          </div>

          <div className="org-account-field">
            <label htmlFor="bio" className="org-account-label">
              Bio
            </label>
            <textarea
              id="bio"
              name="bio"
              className="org-account-textarea"
              value={profileData.bio}
              onChange={handleInputChange}
              placeholder="Share a little about your background and interests..."
              rows={4}
            />
          </div>

          <div className="org-account-social-grid">
            {SOCIAL_FIELDS.map((field) => (
              <div key={field.id} className="org-account-field">
                <label htmlFor={`social-${field.id}`} className="org-account-label">
                  {field.label}
                </label>
                <div className="org-account-input-prefix-wrap">
                  <span className="org-account-input-prefix org-account-input-prefix--muted">
                    {field.prefix}
                  </span>
                  <input
                    id={`social-${field.id}`}
                    type="text"
                    className="org-account-input org-account-input--prefixed"
                    placeholder=""
                    readOnly
                    aria-readonly="true"
                  />
                </div>
              </div>
            ))}
          </div>

          <button type="submit" className="org-account-save-btn">
            Save changes
          </button>
        </section>
      </form>

      <hr className="org-account-divider" />

      <section className="org-account-section">
        <div className="org-account-section-header">
          <h2 className="org-account-section-title org-account-section-title--sm">Emails</h2>
          <span className="org-account-text-link">+ Add email</span>
        </div>
        <p className="org-account-muted">
          Add additional emails to receive event invitations sent to those addresses.
        </p>
        <div className="org-account-card org-account-card--row">
          <span className="org-account-card-title">{profileData.email || '—'}</span>
          <span className="org-account-badge org-account-badge--neutral">Primary</span>
        </div>
        <p className="org-account-muted org-account-muted--small">
          This email will be shared with hosts when you register for their events.
        </p>
      </section>

      <hr className="org-account-divider" />

      <section className="org-account-section">
        <h2 className="org-account-section-title org-account-section-title--sm">Mobile number</h2>
        <p className="org-account-muted">
          Manage the mobile number you use to sign in to Occasia and receive SMS/Whatsapp updates.
        </p>
        <div className="org-account-phone-row">
          <div className="org-account-phone-input">
            <span className="org-account-flag" aria-hidden="true">
              +44
            </span>
            <input
              id="phone"
              name="phone"
              type="tel"
              className="org-account-input org-account-input--plain"
              value={profileData.phone}
              onChange={handleInputChange}
              placeholder="20 7123 4567"
            />
          </div>
          <button type="button" className="org-account-outline-btn" onClick={handleProfileUpdate}>
            Update
          </button>
        </div>
      </section>

      <hr className="org-account-divider" />

      <section className="org-account-section">
        <h2 className="org-account-section-title org-account-section-title--sm">Password &amp; Security</h2>
        <div className="org-account-stack-card">
          {SECURITY_ROWS.map((row, index) => (
            <React.Fragment key={row.title}>
              {index > 0 ? <hr className="org-account-stack-divider" /> : null}
              <div className="org-account-stack-row">
                <div className="org-account-stack-copy">
                  <p className="org-account-card-title">{row.title}</p>
                  <p className="org-account-muted">{row.description}</p>
                </div>
                <span className="org-account-outline-btn org-account-outline-btn--static">
                  {row.action}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </section>

      <hr className="org-account-divider" />

      <section className="org-account-section">
        <h2 className="org-account-section-title org-account-section-title--sm">Third party accounts</h2>
        <p className="org-account-muted">
          Link your accounts to sign in to Occasia and automate your workflows.
        </p>
        <div className="org-account-third-party-grid">
          {THIRD_PARTY_ROWS.map((row, index) => {
            const isLinked = row.linked === 'google' ? googleLinked : Boolean(row.linked);
            return (
            <div key={`${row.name}-${index}`} className="org-account-card org-account-card--third-party">
              <div className="org-account-third-party-icon" aria-hidden="true">
                {row.name.charAt(0)}
              </div>
              <div className="org-account-third-party-copy">
                <p className="org-account-card-title">{row.name}</p>
                <p className="org-account-muted">
                  {isLinked ? profileData.email || 'Linked' : 'Not linked'}
                </p>
              </div>
              {isLinked ? (
                <span className="org-account-linked-mark" aria-hidden="true">
                  ✓
                </span>
              ) : (
                <span className="org-account-add-btn" aria-hidden="true">
                  +
                </span>
              )}
            </div>
            );
          })}
        </div>
      </section>

      <section className="org-account-section">
        <h2 className="org-account-section-title org-account-section-title--sm">Account Syncing</h2>
        <div className="org-account-stack-card">
          <div className="org-account-stack-row">
            <div className="org-account-stack-copy">
              <p className="org-account-card-title">Calendar Syncing</p>
              <p className="org-account-muted">
                Sync your bookings events with your Google, Outlook, or Apple calendar.
              </p>
            </div>
            <span className="org-account-outline-btn org-account-outline-btn--static">
              Add iCal subscription
            </span>
          </div>
          <hr className="org-account-stack-divider" />
          <div className="org-account-stack-row">
            <div className="org-account-stack-copy">
              <p className="org-account-card-title">Sync Contacts with Google</p>
              <p className="org-account-muted">
                Sync your Gmail contacts to easily invite them to your events.
              </p>
            </div>
            <span className="org-account-outline-btn org-account-outline-btn--static">
              Enable syncing
            </span>
          </div>
        </div>
      </section>

      <hr className="org-account-divider" />

      <section className="org-account-section">
        <h2 className="org-account-section-title">Active devices</h2>
        <p className="org-account-muted">
          You are currently signed into Occasia on the following devices.
        </p>
        <div className="org-account-card org-account-card--device">
          <span className="org-account-device-icon" aria-hidden="true">
          ◫
        </span>
          <div className="org-account-device-copy">
            <div className="org-account-device-title-row">
              <span className="org-account-card-title">Chrome on macOS</span>
              <span className="org-account-badge org-account-badge--success">This device</span>
            </div>
            <p className="org-account-muted">London, UK</p>
          </div>
        </div>
      </section>

      <hr className="org-account-divider" />

      <section className="org-account-section org-account-section--delete">
        <div className="org-account-delete-copy">
          <h2 className="org-account-section-title">Delete Account</h2>
          <p className="org-account-muted">
            If you no longer wish to use Occasia, you can permanently delete your account.
          </p>
        </div>
        <span className="org-account-delete-btn">Delete account</span>
      </section>
    </div>
  );
};

export default AccountDetails;
